import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  cn,
  GlassAvatar,
  GlassBadge,
  GlassButton,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { Clock, Download, MapPin, Timer, TrendingUp, Users } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { GET_TIME_LOCATIONS } from '../lib/graphql/operations.js';

import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/* ── Types ── */

interface TimeEntry {
  id: string;
  technicianName: string;
  linkedOrderAk: string | null;
  clockIn: {
    time: string;
    address: string;
  };
  clockOut: {
    time: string;
    address: string;
  } | null;
  durationMinutes: number | null;
  gpsIn: string;
  gpsOut: string | null;
}

interface KpiStatProps {
  label: string;
  value: string;
  numericValue?: number;
  subtext: string;
  icon: LucideIcon;
  highlight?: boolean;
  loading?: boolean;
}

/* ── GraphQL Response Types ── */

interface TimeLocationNode {
  accessKey: number | null;
  user: string | null;
  clockInTime: string | null;
  clockOutTime: string | null;
  clockInLatitude: number | null;
  clockInLongitude: number | null;
  clockOutLatitude: number | null;
  clockOutLongitude: number | null;
  clockInAddress: string | null;
  clockOutAddress: string | null;
  linkedOrderAk: string | null;
}

interface TimeLocationsEdge {
  node: TimeLocationNode;
  cursor?: string;
}

interface TimeLocationsConnection {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: TimeLocationsEdge[];
}

interface GetTimeLocationsResponse {
  timeLocations: TimeLocationsConnection;
}

/* ── Helpers ── */

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${String(h)}h` : `${String(h)}h ${String(m)}m`;
}

function abbreviateGps(coords: string): string {
  return coords.replace(/(\d+\.\d{4})\d*/g, '$1');
}

function techToFilterValue(name: string): string {
  return name.toLowerCase().replace(/\s+/g, '-');
}

function formatCoords(
  lat: number | null | undefined,
  lng: number | null | undefined,
): string | null {
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return null;
  }
  return `${lat.toFixed(4)}°, ${lng.toFixed(4)}°`;
}

function formatTime(isoString: string | null | undefined): string {
  if (isoString === null || isoString === undefined || isoString === '') {
    return '';
  }
  const d = new Date(isoString);
  if (isNaN(d.getTime())) {
    return isoString;
  }
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function computeDurationMinutes(clockIn: string | null, clockOut: string | null): number | null {
  if (clockIn === null || clockOut === null) {
    return null;
  }
  const inMs = new Date(clockIn).getTime();
  const outMs = new Date(clockOut).getTime();
  if (isNaN(inMs) || isNaN(outMs) || outMs <= inMs) {
    return null;
  }
  return Math.round((outMs - inMs) / 60000);
}

function mapNodeToEntry(node: TimeLocationNode): TimeEntry {
  const gpsIn = formatCoords(node.clockInLatitude, node.clockInLongitude);
  const gpsOut = formatCoords(node.clockOutLatitude, node.clockOutLongitude);
  const durationMinutes = computeDurationMinutes(node.clockInTime, node.clockOutTime);
  const hasClockOut = node.clockOutTime !== null;

  return {
    id: node.accessKey !== null ? String(node.accessKey) : '—',
    technicianName: node.user ?? '—',
    linkedOrderAk: node.linkedOrderAk ?? null,
    clockIn: {
      time: formatTime(node.clockInTime),
      address: node.clockInAddress ?? '',
    },
    clockOut: hasClockOut
      ? {
          time: formatTime(node.clockOutTime),
          address: node.clockOutAddress ?? '',
        }
      : null,
    durationMinutes,
    gpsIn: gpsIn ?? '',
    gpsOut: gpsOut,
  };
}

/* ── KPI Stat Card ── */

function KpiStatCard({
  label,
  value,
  numericValue,
  subtext,
  icon: Icon,
  highlight = false,
  loading: isLoading = false,
}: KpiStatProps): ReactElement {
  return (
    <GlassCard>
      <GlassCardContent className="flex items-start justify-between py-5">
        <div>
          <p className="text-sm text-text-secondary mb-1">{label}</p>
          <p className={cn('text-2xl font-semibold', highlight ? 'text-warning-fg' : 'text-text')}>
            {isLoading ? (
              <SkeletonLoader className="w-16 h-7" />
            ) : numericValue !== undefined ? (
              <AnimatedCounter value={numericValue} suffix={value.replace(/[\d.]+/, '')} />
            ) : (
              value
            )}
          </p>
          <p className="mt-1 text-xs text-text-tertiary">{subtext}</p>
        </div>
        <div
          className={cn(
            'flex items-center justify-center w-10 h-10 rounded-lg',
            highlight ? 'bg-warning-soft' : 'bg-primary-soft',
          )}
        >
          <Icon size={20} className={highlight ? 'text-warning-fg' : 'text-primary'} />
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

/* ── Page ── */

export function TimeTrackingPage(): ReactElement {
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [techFilter, setTechFilter] = useState<string>('all');

  const {
    data: rawData,
    loading,
    error,
  } = useQuery(GET_TIME_LOCATIONS, {
    variables: {
      first: 100,
      dateFrom: dateFrom !== '' ? dateFrom : undefined,
      dateTo: dateTo !== '' ? dateTo : undefined,
    },
  });
  const data = rawData as GetTimeLocationsResponse | undefined;

  const TIME_ENTRIES: TimeEntry[] = (data?.timeLocations.edges ?? []).map((e) =>
    mapNodeToEntry(e.node),
  );

  /* Derived stats */
  const activeEntries = TIME_ENTRIES.filter((e) => e.clockOut === null);
  const completedEntries = TIME_ENTRIES.filter(
    (e) => e.clockOut !== null && e.durationMinutes !== null,
  );

  const totalMinutes = completedEntries.reduce((sum, e) => sum + (e.durationMinutes ?? 0), 0);
  const totalHours = (totalMinutes / 60).toFixed(1);
  const activeTechCount = activeEntries.length;
  const avgMinutes = completedEntries.length > 0 ? totalMinutes / completedEntries.length : 0;
  const avgHours = (avgMinutes / 60).toFixed(1);

  const overtimeEntries = completedEntries.filter((e) => (e.durationMinutes ?? 0) > 480);
  const overtimeHours = (
    overtimeEntries.reduce((sum, e) => sum + Math.max(0, (e.durationMinutes ?? 0) - 480), 0) / 60
  ).toFixed(1);

  /* Build technician filter options from loaded data */
  const technicianNames = Array.from(new Set(TIME_ENTRIES.map((e) => e.technicianName)));
  const technicianOptions: readonly { value: string; label: string }[] = [
    { value: 'all', label: 'All Technicians' },
    ...technicianNames.map((name) => ({ value: techToFilterValue(name), label: name })),
  ];

  /* Filtered rows */
  const filteredEntries =
    techFilter === 'all'
      ? TIME_ENTRIES
      : TIME_ENTRIES.filter((e) => techToFilterValue(e.technicianName) === techFilter);

  /* CSV Export */
  function handleExportCSV(): void {
    const headers = [
      'Technician',
      'Clock In',
      'Clock Out',
      'Duration',
      'GPS In',
      'GPS Out',
      'Service Order',
    ];
    const rows = filteredEntries.map((e) => [
      e.technicianName,
      e.clockIn.time,
      e.clockOut?.time ?? 'Active',
      e.durationMinutes !== null ? formatDuration(e.durationMinutes) : '',
      e.gpsIn,
      e.gpsOut ?? '',
      e.linkedOrderAk ?? '',
    ]);
    const csvContent = [headers, ...rows]
      .map((r) => r.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `time-tracking-${dateFrom || 'all'}-to-${dateTo || 'all'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <p className="text-sm text-text-secondary">
          GPS-verified clock in/out records for field technicians
        </p>

        <div className="flex flex-wrap items-end gap-3">
          <GlassInput
            label="From"
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
            }}
            className="w-36"
          />
          <GlassInput
            label="To"
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
            }}
            className="w-36"
          />
          <GlassSelect
            label="Technician"
            options={technicianOptions}
            value={techFilter}
            onChange={(e) => {
              setTechFilter(e.target.value);
            }}
            className="w-44"
          />
          <GlassButton variant="secondary" size="md" onClick={handleExportCSV}>
            <Download size={15} />
            Export CSV
          </GlassButton>
        </div>
      </div>

      {/* ── KPI Summary ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiStatCard
          label="Total Hours"
          value={`${totalHours}h`}
          numericValue={parseFloat(totalHours)}
          subtext={`${String(completedEntries.length)} completed entries`}
          icon={Clock}
          loading={loading}
        />
        <KpiStatCard
          label="Active Technicians"
          value={String(activeTechCount)}
          numericValue={activeTechCount}
          subtext="Currently clocked in"
          icon={Users}
          loading={loading}
        />
        <KpiStatCard
          label="Avg Hours / Tech"
          value={`${avgHours}h`}
          numericValue={parseFloat(avgHours)}
          subtext="Completed shifts only"
          icon={TrendingUp}
          loading={loading}
        />
        <KpiStatCard
          label="Overtime Hours"
          value={`${overtimeHours}h`}
          numericValue={parseFloat(overtimeHours)}
          subtext={`${String(overtimeEntries.length)} technician${overtimeEntries.length === 1 ? '' : 's'} over 8h`}
          icon={Timer}
          highlight={parseFloat(overtimeHours) > 0}
          loading={loading}
        />
      </div>

      {/* ── Data Table ── */}
      <GlassCard>
        <GlassCardHeader>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-text">Time Entries</h2>
            <div className="flex items-center gap-2">
              <GlassBadge variant="success">{activeEntries.length} active</GlassBadge>
              <GlassBadge variant="default">{filteredEntries.length} total</GlassBadge>
            </div>
          </div>
        </GlassCardHeader>

        {loading ? (
          <div className="p-5 space-y-3">
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
            <SkeletonLoader className="h-10 w-full" />
          </div>
        ) : error !== undefined ? (
          <div className="py-12 text-center text-sm text-danger">
            Failed to load time entries: {error.message}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <GlassTable>
              <GlassTableHead>
                <GlassTableRow>
                  <GlassTableHeaderCell>Technician</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Service Order</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Clock In</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Clock Out</GlassTableHeaderCell>
                  <GlassTableHeaderCell>Duration</GlassTableHeaderCell>
                  <GlassTableHeaderCell>GPS (In / Out)</GlassTableHeaderCell>
                </GlassTableRow>
              </GlassTableHead>
              <GlassTableBody>
                {filteredEntries.map((entry) => {
                  const isActive = entry.clockOut === null;
                  const isOvertime = !isActive && (entry.durationMinutes ?? 0) > 480;

                  return (
                    <GlassTableRow key={entry.id}>
                      {/* Technician */}
                      <GlassTableCell>
                        <div className="flex items-center gap-2.5">
                          <GlassAvatar name={entry.technicianName} size="sm" />
                          <span className="font-medium text-text whitespace-nowrap">
                            {entry.technicianName}
                          </span>
                        </div>
                      </GlassTableCell>

                      {/* Service Order */}
                      <GlassTableCell className="font-medium whitespace-nowrap">
                        {entry.linkedOrderAk !== null ? (
                          <Link
                            to={`/service-orders/${entry.linkedOrderAk}`}
                            className="text-primary hover:underline"
                          >
                            {entry.linkedOrderAk}
                          </Link>
                        ) : (
                          <span className="text-text-tertiary">—</span>
                        )}
                      </GlassTableCell>

                      {/* Clock In */}
                      <GlassTableCell>
                        <p className="font-medium text-text whitespace-nowrap">
                          {entry.clockIn.time}
                        </p>
                        <p className="flex items-center gap-1 mt-0.5 text-xs text-text-tertiary">
                          <MapPin size={11} className="shrink-0" />
                          <span className="truncate max-w-[18ch]">{entry.clockIn.address}</span>
                        </p>
                      </GlassTableCell>

                      {/* Clock Out */}
                      <GlassTableCell>
                        {isActive ? (
                          <GlassBadge variant="success">Active</GlassBadge>
                        ) : (
                          <>
                            <p className="font-medium text-text whitespace-nowrap">
                              {entry.clockOut?.time ?? ''}
                            </p>
                            <p className="flex items-center gap-1 mt-0.5 text-xs text-text-tertiary">
                              <MapPin size={11} className="shrink-0" />
                              <span className="truncate max-w-[18ch]">
                                {entry.clockOut?.address ?? ''}
                              </span>
                            </p>
                          </>
                        )}
                      </GlassTableCell>

                      {/* Duration */}
                      <GlassTableCell className="whitespace-nowrap">
                        {isActive ? (
                          <span className="text-text-tertiary italic">In progress</span>
                        ) : (
                          <span
                            className={cn(
                              'font-medium',
                              isOvertime ? 'text-warning-fg' : 'text-text',
                            )}
                          >
                            {formatDuration(entry.durationMinutes ?? 0)}
                            {isOvertime && (
                              <span className="ml-1.5">
                                <GlassBadge variant="warning">OT</GlassBadge>
                              </span>
                            )}
                          </span>
                        )}
                      </GlassTableCell>

                      {/* GPS */}
                      <GlassTableCell>
                        <p className="text-xs font-mono text-text-secondary whitespace-nowrap">
                          {entry.gpsIn !== '' ? abbreviateGps(entry.gpsIn) : '—'}
                        </p>
                        {entry.gpsOut !== null ? (
                          <p className="text-xs font-mono text-text-tertiary whitespace-nowrap mt-0.5">
                            {abbreviateGps(entry.gpsOut)}
                          </p>
                        ) : (
                          <p className="text-xs text-text-tertiary mt-0.5 italic">—</p>
                        )}
                      </GlassTableCell>
                    </GlassTableRow>
                  );
                })}

                {filteredEntries.length === 0 && (
                  <GlassTableRow>
                    <GlassTableCell colSpan={6} className="text-center text-text-tertiary py-10">
                      No time entries match the current filter.
                    </GlassTableCell>
                  </GlassTableRow>
                )}
              </GlassTableBody>
            </GlassTable>
          </div>
        )}
      </GlassCard>
    </div>
  );
}
